"use client";

import { useEffect } from "react";
import { fallbackLng, languages } from "@/lib/i18n/config";

export interface GlobalErrorProps {
  readonly error: Error & { digest?: string };
  readonly reset: () => void;
}

const copy: Record<
  string,
  { title: string; body: string; retry: string; details: string }
> = {
  nl: {
    title: "Er ging iets mis",
    body: "Deze pagina kon niet geladen worden. Probeer het opnieuw.",
    retry: "Opnieuw proberen",
    details: "Technische details",
  },
  en: {
    title: "Something went wrong",
    body: "This page could not be loaded. Try again.",
    retry: "Try again",
    details: "Technical details",
  },
};

function localeFromPathname(): string {
  if (typeof window === "undefined") return fallbackLng;
  const [, segment] = window.location.pathname.split("/");
  return languages.includes(segment) ? segment : fallbackLng;
}

/**
 * Last-resort boundary: replaces the root layout, so it can only rely on
 * inline styles and static copy. Catches throws from the locale layout and
 * its providers, which `[locale]/error.tsx` sits underneath and cannot catch.
 */
export default function GlobalError({
  error,
  reset,
}: GlobalErrorProps): React.ReactElement {
  const locale = localeFromPathname();
  const text = copy[locale] ?? copy[fallbackLng];

  useEffect(() => {
    console.error(error);
  }, [error]);

  return (
    <html lang={locale}>
      <body
        style={{
          fontFamily: "system-ui, sans-serif",
          margin: 0,
          minHeight: "100dvh",
          display: "grid",
          placeItems: "center",
          padding: "2rem",
        }}
      >
        <main style={{ maxWidth: "40rem" }}>
          <h1>{text.title}</h1>
          <p>{text.body}</p>
          <button type="button" onClick={reset} style={{ padding: "0.5rem 1rem" }}>
            {text.retry}
          </button>
          <details style={{ marginBlockStart: "1.5rem" }}>
            <summary style={{ cursor: "pointer" }}>{text.details}</summary>
            <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {error.digest ? `${error.digest}\n` : ""}
              {error.message}
            </pre>
          </details>
        </main>
      </body>
    </html>
  );
}
